import { useState, useRef } from "react";
import { products as prodApi, embedProducts } from "../lib/supabase.js";

const CORE_FIELDS = [
  { key: "name", label: "Name", required: true, aliases: ["name", "title", "product_name", "product_title", "product"] },
  { key: "sku", label: "SKU", aliases: ["sku", "part_number", "part_no", "item_number", "model", "model_number"] },
  { key: "category", label: "Category", aliases: ["category", "product_category", "type", "product_type", "group"] },
  { key: "short_description", label: "Short Desc", aliases: ["short_description", "summary", "short_desc", "tagline", "subtitle"] },
  { key: "description", label: "Description", aliases: ["description", "long_description", "details", "body", "content"] },
  { key: "image_url", label: "Image URL", aliases: ["image_url", "image", "photo", "thumbnail", "img", "picture", "main_image"] },
  { key: "product_url", label: "Product URL", aliases: ["product_url", "url", "link", "product_link", "page_url", "web_url"] },
  { key: "price", label: "Price", aliases: ["price", "cost", "msrp", "retail_price", "unit_price"] },
  { key: "price_label", label: "Price Label", aliases: ["price_label", "price_type"] },
  { key: "features", label: "Features", aliases: ["features", "key_features", "highlights"] },
  { key: "tags", label: "Tags", aliases: ["tags", "keywords", "labels"] },
];

const SKIP_COLUMNS = ["id", "created_at", "updated_at", "embedding", "embedding_text"];

function autoMapColumns(headers) {
  const mapping = {};
  const normalized = headers.map((h) => h.toLowerCase().trim().replace(/\s+/g, "_"));
  for (const field of CORE_FIELDS) {
    const idx = normalized.findIndex((h) => field.aliases.includes(h));
    if (idx >= 0) mapping[headers[idx]] = field.key;
  }
  for (const h of headers) { if (SKIP_COLUMNS.includes(h.toLowerCase().trim())) mapping[h] = ""; }
  return mapping;
}

function parseCSV(text) {
  // Proper CSV parser that handles multiline quoted fields, escaped quotes, etc.
  const rows = [];
  let current = [];
  let cell = "";
  let inQuotes = false;
  
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    const next = text[i + 1];
    
    if (inQuotes) {
      if (ch === '"' && next === '"') {
        // Escaped quote
        cell += '"';
        i++;
      } else if (ch === '"') {
        // End of quoted field
        inQuotes = false;
      } else {
        cell += ch;
      }
    } else {
      if (ch === '"' && cell === "") {
        // Start of quoted field
        inQuotes = true;
      } else if (ch === ',') {
        current.push(cell.trim());
        cell = "";
      } else if (ch === '\n' || (ch === '\r' && next === '\n')) {
        current.push(cell.trim());
        if (current.some((c) => c)) rows.push(current);
        current = [];
        cell = "";
        if (ch === '\r') i++; // skip \n after \r
      } else if (ch === '\r') {
        current.push(cell.trim());
        if (current.some((c) => c)) rows.push(current);
        current = [];
        cell = "";
      } else {
        cell += ch;
      }
    }
  }
  // Flush last row
  if (cell || current.length > 0) {
    current.push(cell.trim());
    if (current.some((c) => c)) rows.push(current);
  }
  
  if (rows.length < 2) return { headers: [], rows: [] };
  
  const headers = rows[0];
  const dataRows = rows.slice(1)
    .filter((r) => r.length > 0)
    .map((r) => {
      const obj = {};
      headers.forEach((h, i) => { obj[h] = r[i] || ""; });
      return obj;
    })
    .filter((r) => Object.values(r).some((v) => v));
  
  // Deduplicate by name (case-insensitive) — keep first occurrence
  const seen = new Set();
  const deduped = dataRows.filter((r) => {
    const nameCol = Object.keys(r).find((k) => 
      k.toLowerCase().match(/^(name|title|product_name|product_title|product)$/)
    );
    if (!nameCol || !r[nameCol]) return true; // can't dedup without name, let validation handle it
    const key = r[nameCol].toLowerCase().trim();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  
  return { headers, rows: deduped };
}

export default function ProductCSVImporter({ color, onComplete, onClose }) {
  const [step, setStep] = useState("upload");
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [columnMap, setColumnMap] = useState({});
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const { headers: h, rows: r } = parseCSV(ev.target.result);
      if (h.length === 0) return;
      setHeaders(h);
      setRows(r);
      setColumnMap(autoMapColumns(h));
      setStep("mapping");
    };
    reader.readAsText(file);
  };

  const mapColumn = (csvCol, targetKey) => setColumnMap((p) => ({ ...p, [csvCol]: targetKey }));

  const getMappedCount = () => Object.values(columnMap).filter((v) => v).length;
  const hasName = Object.values(columnMap).includes("name");

  const handleImport = async () => {
    setImporting(true);
    const mapped = rows.map((row) => {
      const product = { is_active: true };
      const extras = {};
      for (const [csvCol, targetKey] of Object.entries(columnMap)) {
        if (!targetKey || !row[csvCol]) continue;
        const val = row[csvCol].trim();
        if (!val) continue;
        if (targetKey === "price") {
          product.price = parseFloat(val.replace(/[^0-9.]/g, "")) || null;
        } else if (targetKey === "features") {
          product.features = val.split(/[;|]/).map((f) => f.trim()).filter(Boolean);
        } else if (targetKey === "tags") {
          product.tags = val.split(/[,;|]/).map((t) => t.trim()).filter(Boolean);
        } else if (CORE_FIELDS.some((f) => f.key === targetKey)) {
          product[targetKey] = val;
        } else {
          extras[targetKey] = val;
        }
      }
      // Unmapped columns go into attributes
      for (const csvCol of headers) {
        if (columnMap[csvCol] !== undefined) continue; // mapped or skipped
        const val = row[csvCol]?.trim();
        if (val) extras[csvCol] = val;
      }
      if (Object.keys(extras).length > 0) product.attributes = extras;
      // Build embedding text
      product.embedding_text = [product.name, product.sku, product.category, product.short_description, product.description, ...(product.features || []), ...(product.tags || [])].filter(Boolean).join(" ");
      return product;
    }).filter((p) => {
      if (!p.name) return false;
      const name = p.name;
      // Reject names that look like code/scripts/JSON/HTML
      if (name.length > 500) return false;
      if (/^\s*[\{\[\(<]/.test(name)) return false;                  // starts with { [ ( <
      if (/function\s*\(|=>|var\s|const\s|let\s/.test(name)) return false;  // JS code
      if (/CDATA|<script|<\/|<div|<span|<style/.test(name)) return false;  // HTML/XML
      if (/ShopifyBuy|shopify|onReady|createElement/.test(name)) return false; // Shopify embed code
      if (/^\d{10,}$/.test(name)) return false;                     // pure long numeric ID
      if (/^(id|options|styles|product)\s*:/.test(name)) return false; // JSON-like keys
      if (/%[0-9A-F]{2}/.test(name) && name.length > 50) return false; // URL-encoded junk
      return true;
    });

    try {
      const batchSize = 50;
      let imported = 0;
      for (let i = 0; i < mapped.length; i += batchSize) {
        const batch = mapped.slice(i, i + batchSize);
        const { error } = await prodApi.createMany(batch);
        if (error) throw error;
        imported += batch.length;
      }
      setResult({ success: true, count: imported });
      setStep("done");
      onComplete?.();
      // Trigger embedding generation in background for all new products
      embedProducts({ limit: 200 }).catch(() => {});
    } catch (err) {
      setResult({ success: false, error: err.message });
      setStep("done");
    }
    setImporting(false);
  };

  const allTargets = [...CORE_FIELDS.map((f) => ({ key: f.key, label: f.label })), { key: "", label: "— Skip —" }];

  const iS = { width: "100%", padding: "8px 12px", background: "#0a0a10", border: "1px solid #1e1e2e", borderRadius: 6, color: "#ccc", fontSize: 13, outline: "none", fontFamily: "inherit", boxSizing: "border-box" };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 900, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#111118", border: "1px solid #1e1e2e", borderRadius: 14, padding: 28, width: 700, maxHeight: "85vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Import Products from CSV</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#555", fontSize: 18, cursor: "pointer" }}>✕</button>
        </div>

        {step === "upload" && (
          <div style={{ textAlign: "center", padding: "40px 20px" }}>
            <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.3 }}>📄</div>
            <div style={{ fontSize: 13, color: "#888", marginBottom: 20 }}>Upload a CSV file with your product catalog</div>
            <input ref={fileRef} type="file" accept=".csv,.tsv,.txt" onChange={handleFile} style={{ display: "none" }} />
            <button onClick={() => fileRef.current?.click()} style={{ padding: "10px 24px", background: color, border: "none", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Choose File</button>
            <div style={{ fontSize: 11, color: "#444", marginTop: 12, lineHeight: 1.6 }}>Expected columns: Name, SKU, Category, Description, Image URL, Price, Features, Tags, Product URL</div>
          </div>
        )}

        {step === "mapping" && (
          <>
            <div style={{ fontSize: 12, color: "#888", marginBottom: 16 }}>{rows.length} unique product rows found. Map your CSV columns to product fields:</div>
            <div style={{ maxHeight: 400, overflowY: "auto", marginBottom: 20 }}>
              {headers.map((h) => (
                <div key={h} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 12px", background: "#0a0a10", borderRadius: 6, marginBottom: 4 }}>
                  <div style={{ width: 180, fontSize: 12, color: "#ccc", fontFamily: "monospace" }}>{h}</div>
                  <div style={{ fontSize: 12, color: "#333" }}>→</div>
                  <select value={columnMap[h] ?? ""} onChange={(e) => mapColumn(h, e.target.value)} style={{ ...iS, flex: 1, cursor: "pointer" }}>
                    <option value="">— Skip —</option>
                    {CORE_FIELDS.map((f) => <option key={f.key} value={f.key}>{f.label}{f.required ? " *" : ""}</option>)}
                  </select>
                  <div style={{ fontSize: 10, color: "#555", width: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{rows[0]?.[h] || ""}</div>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: 12, color: "#666" }}>{getMappedCount()} columns mapped{!hasName && <span style={{ color: "#f87171", marginLeft: 8 }}>⚠ Name is required</span>}</div>
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={() => setStep("upload")} style={{ padding: "10px 20px", background: "#1a1a2a", border: "1px solid #2a2a3a", borderRadius: 8, color: "#888", fontSize: 13, cursor: "pointer" }}>Back</button>
                <button onClick={handleImport} disabled={!hasName || importing} style={{ padding: "10px 24px", background: color, border: "none", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 600, cursor: hasName && !importing ? "pointer" : "not-allowed", opacity: hasName && !importing ? 1 : 0.5 }}>{importing ? `Importing ${rows.length}...` : `Import ${rows.length} Products`}</button>
              </div>
            </div>
          </>
        )}

        {step === "done" && (
          <div style={{ textAlign: "center", padding: "40px 20px" }}>
            {result?.success ? (
              <>
                <div style={{ fontSize: 32, marginBottom: 12 }}>✅</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#22c55e", marginBottom: 8 }}>Imported {result.count} products!</div>
                <div style={{ fontSize: 12, color: "#666", marginBottom: 20 }}>Products are ready in your catalog.</div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 32, marginBottom: 12 }}>❌</div>
                <div style={{ fontSize: 14, color: "#f87171", marginBottom: 8 }}>Import failed</div>
                <div style={{ fontSize: 12, color: "#666", marginBottom: 20 }}>{result?.error}</div>
              </>
            )}
            <button onClick={onClose} style={{ padding: "10px 24px", background: color, border: "none", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Done</button>
          </div>
        )}
      </div>
    </div>
  );
}
